import { SUPPORT_TRAIL_MAX, type SupportTrail, type SupportTrailItem } from "@genbaryoku/contract";

/**
 * 「？」を押す前の足あと（#144、docs/53 §8.4）。問い合わせに添える「画面の版」と「直前の操作 20件」。
 *   - 版：Vite の build に埋めた git のコミット（vite.config.ts の __GB_BUILD__。開発中は "dev"）
 *   - 操作：開いた画面（パス。id は :id に伏せる）／押したボタン（ラベルだけ。入力した内容・打った文字は入れない）／
 *     GET 以外か 2xx でない API（method・パス・応答コード）／画面のエラー（文の先頭 200字）
 *   - メモリの中の輪（20件）。送るのはログイン後の「？」を押したときだけ。「？」の窓の中（data-nocapture）の操作は入れない
 */
declare const __GB_BUILD__: string | undefined;
export const BUILD: string = typeof __GB_BUILD__ === "string" && __GB_BUILD__ ? __GB_BUILD__ : "dev";

const items: SupportTrailItem[] = [];
let installed = false;

/** 開いている画面のパス（id は伏せる。API でももう一度伏せる） */
export function screenPath(path = location.pathname): string {
  return path
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
    .replace(/\/[0-9]{3,}(?=\/|$)/g, "/:n")
    .slice(0, 200);
}

export function record(kind: SupportTrailItem["kind"], text: string): void {
  const last = items[items.length - 1];
  const t = text.replace(/\s+/g, " ").trim().slice(0, 200);
  if (!t) return;
  // 同じ画面の読み直しなど、直前と同じものは重ねない
  if (last && last.kind === kind && last.text === t && Date.now() - Date.parse(last.t) < 2000) return;
  items.push({ t: new Date().toISOString(), kind, text: t });
  while (items.length > SUPPORT_TRAIL_MAX) items.shift();
}

/** 画面が変わった（BackTracker から） */
export const recordNav = (path: string): void => record("nav", screenPath(path));
/** API の結果（api.ts から）。GET の 2xx は数が多いので入れない */
export function recordApi(method: string, path: string, status: number): void {
  if (method === "GET" && status >= 200 && status < 300) return;
  record("api", `${method} ${screenPath(path.split(/[?#]/)[0] ?? "")} → ${status}`);
}

/** 押したものの名前（aria-label → 文字。長いものは切る。入力の欄・リンクの先の文字は入れない） */
function labelOf(el: Element): string | null {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;
  if (el instanceof HTMLInputElement) {
    if (el.type === "checkbox" || el.type === "radio") return el.labels?.[0]?.textContent?.trim() || el.name || el.type;
    return null;
  }
  if (el instanceof HTMLSelectElement) return el.labels?.[0]?.textContent?.trim() || el.name || "select";
  const text = el.textContent?.trim() ?? "";
  return text.length > 0 && text.length <= 40 ? text : el.getAttribute("title") || (text ? `${text.slice(0, 24)}…` : null);
}

export function installTrail(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const el = target?.closest("button,[role=button],summary,a[href],input[type=checkbox],input[type=radio],select");
      if (!el || el.closest("[data-nocapture]")) return;
      const label = labelOf(el);
      if (label) record("click", `${el instanceof HTMLAnchorElement ? "リンク" : "押す"}：${label}`);
    },
    true,
  );
  window.addEventListener("error", (e) => record("error", e.message || String(e.error ?? "error")));
  window.addEventListener("unhandledrejection", (e) => {
    const r: unknown = e.reason;
    record("error", r instanceof Error ? `${r.name}: ${r.message}` : String(r));
  });
}

/** 「？」に添える形（直前の操作は古い順） */
export function currentTrail(): SupportTrail {
  return { build: BUILD, items: items.slice() };
}

/** テスト用：空にする */
export function resetTrail(): void {
  items.length = 0;
}
